import { useCallback, useRef } from "react"
import { useMutation } from "@apollo/client"
import debounce from "lodash/debounce"
import { MarkNotificationsAsSeenAndReadDocument } from "@/gql/graphql"
import { SeenNotification } from "./Notifications"

export const useNotifications = () => {
  const [markAsSeenAndRead] = useMutation(MarkNotificationsAsSeenAndReadDocument)
  // Since virtually all of our notifications aren't able to be clicked into,
  // our badge counts are best to be based on the "isSeen" property instead of the "isRead" property.
  // But for now, we will also mark them as read when they're viewed in case we pivot to using "isRead" in the future.
  // If in the future we do decide to use `isRead`, we would exclude the `notification_type`s that require interaction
  // from being auto marked as read while keeping the same logic for `isSeen`.
  const handleViewableItemsChanged = useCallback(
    async (notifications: SeenNotification[]) => {
      // TODO: Exclude notifications that must be tapped into/interacted with from being auto marked as read
      const viewableItemIdentifiers = notifications
        .filter(notif => notif.isSeen === false)
        .map(notif => ({ id: notif.id, group: notif.group }))

      const viewableItemIds = viewableItemIdentifiers.map(item => item.id)
      // Mark as seen and read + optimistically update the cache
      await markAsSeenAndRead({
        variables: { activityIds: viewableItemIds }, // Use `id` here, not group
        update: cache => {
          for (const { group } of viewableItemIdentifiers) {
            const id = cache.identify({
              __typename: "StreamNotificationFeedAggregatedActivity",
              group,
            })
            cache.modify({
              id,
              fields: {
                isSeen: () => true,
                // TODO: Add back once being used
                // isRead: () => true
              },
            })
          }
        },
      })
    },
    [markAsSeenAndRead]
  )

  const debouncedHandleViewableItemsChanged = useRef(debounce(handleViewableItemsChanged, 250)).current

  return {
    handleViewableItemsChanged: debouncedHandleViewableItemsChanged,
  }
}
